import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import * as pb from '@/generated/console_pb'
import { setUnauthenticatedHandler } from '@/lib/grpcweb/client'
import { whoAmI } from '@/lib/grpcweb/console-api'
import {
  clearSession,
  getSessionCsrf,
  getStoredApiKey,
  hasStoredSession,
  setSessionCsrf,
  storeApiKey,
  type StorageTier,
} from '@/lib/auth/session'
import {
  changePassword as changePasswordHttp,
  login as passwordLoginHttp,
  logout as logoutHttp,
  LoginError,
} from '@/lib/auth/auth-http'

export type AuthStatus = 'checking' | 'unauthenticated' | 'authenticated'

// ADR-118 Phase 5D (#1258): WhoAmI replaces the prior ListOperators-probe
// heuristic entirely. WhoAmI returns the caller's own authenticated
// principal directly -- {id, email, role} for an operator Bearer key --
// so role is now a real capability read, not an inference from whether an
// unrelated admin-only RPC happened to succeed. See console.proto's WhoAmI
// doc for why the old probe was wrong for a viewer operator (who legitimately
// passes some List* gates) and could never work at all for an instance
// principal.
export type ViewerRole = 'admin' | 'viewer'

export interface Principal {
  id: string
  email: string
  role: ViewerRole
}

interface AuthContextValue {
  status: AuthStatus
  role: ViewerRole | null
  principal: Principal | null
  isAdmin: boolean
  // login is the API-KEY path (operator pastes a console_ key). Preserved
  // unchanged — a key is still a first-class credential (ADR-119 D2).
  login: (apiKey: string, remember: boolean) => Promise<void>
  // loginWithPassword is the PASSWORD path (ADR-118/ADR-119 Phase 7): POST
  // /auth/login sets the HttpOnly session cookie + returns the CSRF token,
  // then WhoAmI populates the principal exactly like a key login. Throws a
  // generic error on bad credentials (no enumeration).
  loginWithPassword: (email: string, password: string) => Promise<void>
  // logout is mode-aware: in cookie mode it hits /auth/logout to revoke the
  // server session; in key mode it just drops the stored key. Either way it
  // clears all local session state and returns to unauthenticated.
  logout: () => Promise<void>
  // changePassword is the cookie-mode account action: POST /auth/change-password
  // with the CSRF header. The server revokes all sessions on success, so this
  // resolves by logging the SPA out (back to the login screen).
  changePassword: (oldPassword: string, newPassword: string) => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

function toPrincipal(res: pb.WhoAmIResponse): Principal {
  const op = res.getOperator()
  if (!op) {
    // The Console UI only ever authenticates operators (instance
    // principals never reach this browser app), so a response with no
    // operator half populated means the server considered this call an
    // instance-principal call -- not a shape this UI's login flow expects.
    throw new Error('WhoAmI returned no operator principal for an operator session')
  }
  return {
    id: op.getId(),
    email: op.getEmail(),
    role:
      op.getRole() === pb.ConsoleOperatorRole.CONSOLE_OPERATOR_ROLE_ADMIN
        ? 'admin'
        : 'viewer',
  }
}

async function fetchPrincipal(): Promise<Principal> {
  const res = await whoAmI()
  return toPrincipal(res)
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('checking')
  const [principal, setPrincipal] = useState<Principal | null>(null)

  const goUnauthenticated = useCallback(() => {
    setStatus('unauthenticated')
    setPrincipal(null)
  }, [])

  useEffect(() => {
    setUnauthenticatedHandler(goUnauthenticated)
  }, [goUnauthenticated])

  useEffect(() => {
    if (!hasStoredSession()) {
      setStatus('unauthenticated')
      return
    }
    fetchPrincipal()
      .then((p) => {
        setPrincipal(p)
        setStatus('authenticated')
      })
      .catch(() => {
        // callConsole's 401 handler already cleared the session when the
        // failure was Unauthenticated; for any other failure (e.g.
        // network), fail closed to unauthenticated rather than granting
        // access on doubt.
        clearSession()
        setStatus('unauthenticated')
      })
  }, [])

  const login = useCallback(async (apiKey: string, remember: boolean) => {
    const tier: StorageTier = remember ? 'local' : 'session'
    // Store first so callConsole (which reads getStoredApiKey()) picks it
    // up for the WhoAmI call.
    storeApiKey(apiKey, tier)
    try {
      const p = await fetchPrincipal()
      setPrincipal(p)
      setStatus('authenticated')
    } catch (err) {
      clearSession()
      setStatus('unauthenticated')
      throw err
    }
  }, [])

  const loginWithPassword = useCallback(async (email: string, password: string) => {
    // POST /auth/login sets the HttpOnly session cookie (via credentials:
    // include in auth-http) and returns the CSRF token + identity. Store the
    // CSRF token in memory FIRST so callConsole (cookie mode) can attach it to
    // the WhoAmI call's metadata if needed, then populate the principal exactly
    // like a key login.
    let res
    try {
      res = await passwordLoginHttp(email.trim(), password)
    } catch (err) {
      // Anti-enumeration: a 401 is the generic invalid-credentials case
      // (unknown email / wrong password / disabled operator all collapse to it
      // server-side). Surface a single generic message. For any OTHER status
      // (e.g. 400 validation, 500) surface the server's message so a genuine
      // input/backend problem is legible.
      if (err instanceof LoginError && err.status === 401) {
        throw new Error('Invalid email or password.')
      }
      throw err instanceof Error ? err : new Error('Login failed. Try again.')
    }
    setSessionCsrf(res.csrfToken)
    try {
      const p = await fetchPrincipal()
      setPrincipal(p)
      setStatus('authenticated')
    } catch (err) {
      // The login succeeded but WhoAmI failed — clear the half-established
      // session and fail closed. clearSession also drops the in-memory CSRF.
      clearSession()
      setStatus('unauthenticated')
      throw err
    }
  }, [])

  const logout = useCallback(async () => {
    // Cookie mode: revoke the server-side session (best-effort, always
    // resolves) before clearing local state. Key mode: nothing server-side to
    // revoke (a key is stateless bearer auth), so just drop the stored key.
    // Detect via getStoredApiKey — a stored key means key mode; otherwise a
    // cookie session (or nothing, in which case logoutHttp is a harmless no-op
    // the server 200s anyway).
    if (!getStoredApiKey()) {
      await logoutHttp()
    }
    clearSession()
    goUnauthenticated()
  }, [goUnauthenticated])

  const changePassword = useCallback(
    async (oldPassword: string, newPassword: string) => {
      const csrf = getSessionCsrf()
      if (!csrf) {
        // Only a cookie session can change its password (the endpoint is
        // session+CSRF gated). A key-mode operator has no password session.
        throw new Error('Password change is only available for a password login session.')
      }
      await changePasswordHttp(oldPassword, newPassword, csrf)
      // The server revoked ALL sessions (including this one) and cleared the
      // cookies. Tear down locally and return to login so the operator
      // re-authenticates with the NEW password.
      clearSession()
      goUnauthenticated()
    },
    [goUnauthenticated],
  )

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      role: principal?.role ?? null,
      principal,
      isAdmin: principal?.role === 'admin',
      login,
      loginWithPassword,
      logout,
      changePassword,
    }),
    [status, principal, login, loginWithPassword, logout, changePassword],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return ctx
}

// Exposed for tests that want to bypass the real network path.
export const __testing = { fetchPrincipal, getStoredApiKey }
